/**
 * Carga bajo demanda el script del Botón de pagos de Bold y abre la pasarela.
 *
 * A diferencia de Wompi, que es una URL de redirección armada en el backend,
 * Bold expone un constructor global (BoldCheckout) que recibe los parámetros
 * de la venta y abre la pasarela al llamar a .open().
 *
 * Los parámetros (orderId, currency, amount, apiKey, integritySignature,
 * redirectionUrl) los arma BoldPaymentService en api-menu-digital y llegan
 * en respuestaJSON.data.pay.widget_params. El hash de integridad se calcula
 * SIEMPRE en el backend: acá solo viaja la llave de identidad, que es publica.
 *
 * Documentacion oficial:
 * https://developers.bold.co/pagos-en-linea/boton-de-pagos/integracion-manual/integracion-personalizada
 */

const BOLD_SCRIPT_SRC = 'https://checkout.bold.co/library/boldPaymentButton.js';

/**
 * Inserta el script de Bold en el head si no esta ya cargado.
 * @returns {Promise<void>} resuelve cuando window.BoldCheckout esta disponible
 */
export const cargarScriptBold = () =>
  new Promise((resolve, reject) => {
    if (window.BoldCheckout) {
      resolve();
      return;
    }

    const existente = document.querySelector(`script[src="${BOLD_SCRIPT_SRC}"]`);
    if (existente) {
      existente.addEventListener('load', () => resolve());
      existente.addEventListener('error', () =>
        reject(new Error('No se pudo cargar el script de Bold'))
      );
      return;
    }

    const script = document.createElement('script');
    script.src = BOLD_SCRIPT_SRC;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('No se pudo cargar el script de Bold'));
    document.head.appendChild(script);
  });

/**
 * Abre la pasarela de Bold con los parametros que devolvio el backend.
 *
 * @param {Object} widgetParams - objeto pay.widget_params de la respuesta del API
 * @returns {Promise<void>} rechaza si falta algun parametro o si el script no carga
 */
export const abrirCheckoutBold = async (widgetParams) => {
  const requeridos = [
    'orderId',
    'currency',
    'amount',
    'apiKey',
    'integritySignature',
    'redirectionUrl',
  ];

  const faltantes = requeridos.filter((campo) => !widgetParams?.[campo]);
  if (faltantes.length > 0) {
    throw new Error(`Faltan parametros de Bold: ${faltantes.join(', ')}`);
  }

  await cargarScriptBold();

  if (!window.BoldCheckout) {
    throw new Error('BoldCheckout no quedo disponible tras cargar el script');
  }

  const checkout = new window.BoldCheckout({
    orderId: widgetParams.orderId,
    currency: widgetParams.currency,
    amount: widgetParams.amount,
    apiKey: widgetParams.apiKey,
    integritySignature: widgetParams.integritySignature,
    redirectionUrl: widgetParams.redirectionUrl,
  });

  checkout.open();
};
